import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { CourseProgressTotalsData } from 'teacher-ui-client/components/giraffe/charts/course-progress/totals'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import fillMonths from 'teacher-ui-client/utils/student-events/fill-months'

export interface CourseProgressTotalsResponse {
  course_progress_totals_summary_by_grade: SummaryData[]
  course_progress_totals_by_grade_by_month: MonthData[]
}

interface SummaryData {
  grade_position: number
  data: [
    { category: 'total_count'; count: number },
    { category: 'student_count'; count: number },
    { category: 'student_average'; average: number },
  ]
}

interface MonthData {
  grade_position: number
  months: {
    month: string
    count: number
  }[]
}

export async function courseProgressTotals(
  this: StudentEventsService,
  product: LegacyProductRe | LegacyProductRex | LegacyProductMs,
  precinct: LegacyPrecinctLessons | LegacyPrecinctMyLessons | LegacyPrecinctReading | LegacyPrecinctSpelling,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<CourseProgressTotalsData[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('course_progress_totals_by_grade_by_month', product, statsScope)
  const query = this.buildQuery(statsScope, period, { precinct })

  const data = await this.fetchData<CourseProgressTotalsResponse>(url, query)
  if (!data) return null

  const { course_progress_totals_summary_by_grade: summaryData, course_progress_totals_by_grade_by_month: monthData } =
    data

  if (monthData.length === 0) {
    return null
  }

  return groupByGrade(summaryData, monthData, period)
}

function groupByGrade(
  summaryData: SummaryData[],
  monthData: MonthData[],
  period: NormalizedInterval,
): CourseProgressTotalsData[] {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummaryData
    .map((summary) => {
      const gradeMonthData = monthData.find((month) => month.grade_position === summary.grade_position)
      if (!gradeMonthData) return null

      const total_count = summary.data.find((value) => value.category === 'total_count')!.count
      const student_count = summary.data.find((value) => value.category === 'student_count')!.count
      const student_average = summary.data.find((value) => value.category === 'student_average')!.average
      const filledMonths = fillMonths(gradeMonthData.months, period, {
        count: 0,
      })

      return {
        grade_position: summary.grade_position,
        summary: {
          total_count,
          student_count,
          student_average,
        },
        months: filledMonths,
      }
    })
    .filter((data) => data !== null)
}
